import { nextTick } from '@patrianna/shared-utils'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { getRedemptionHistory } from '@patrianna-payments/shared-store/redeem/store/actions'
import { getRedemptionHistorySelector } from '@patrianna-payments/shared-store/redeem/store/selectors'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import ContentBlock from 'pageContainers/TransactionsPage/components/ContentBlock'
import DateFilterLimitInfo from 'pageContainers/TransactionsPage/components/FiltersBlock/components/DateFilterLimitInfo'
import {
  getDateRangeUTC,
  getQueryStringToSetDateRangeToQuery,
  getValidatedDateRange,
} from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import type { DateRangeProps } from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import PaginationBlock from 'pageContainers/TransactionsPage/components/PaginationBlock'
import TimeTable from 'pageContainers/TransactionsPage/components/RedemptionTab/components/TimeTable'
import TransactionItems from 'pageContainers/TransactionsPage/components/RedemptionTab/components/TransactionItems'
import content from 'prismic-translates/transactions-history-page.json'
import RichText from 'src/components/RichText'
import FiltersBlock from 'src/pageContainers/TransactionsPage/components/FiltersBlock'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { removeQueryByKeys } from 'utils/searchParams'

const t = getTranslationFromJson([content])

export const PAGE_SIZE = 10
export const FIRST_PAGE = 1

export default function RedemptionTab() {
  const dispatch = useAppDispatch()
  const { pagination, isLoading } = useAppSelector(getRedemptionHistorySelector)
  const [dateRange, setDateRange] = useState<DateRangeProps>(getValidatedDateRange())

  const { replace } = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const isPaginated = pagination?.totalPages > FIRST_PAGE && !isLoading

  useEffect(() => {
    nextTick(() => {
      dispatch(
        getRedemptionHistory({
          dateRange: getDateRangeUTC(getValidatedDateRange()),
          pageRequest: { pageNumber: FIRST_PAGE, pageSize: PAGE_SIZE },
        }),
      )

      if (searchParams.has('startDate') || searchParams.has('endDate')) {
        replace(`${pathname}${removeQueryByKeys({ query: searchParams, wholeNameKeys: ['startDate', 'endDate'] })}`)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChange = (pageNumber: number) => {
    dispatch(
      getRedemptionHistory({ dateRange: getDateRangeUTC(dateRange), pageRequest: { pageNumber, pageSize: PAGE_SIZE } }),
    )
    window?.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }

  const handleFiltersChange = (range: DateRangeProps) => {
    const newDateRange = getValidatedDateRange(range)

    setDateRange(newDateRange)
    dispatch(
      getRedemptionHistory({
        dateRange: getDateRangeUTC(newDateRange),
        pageRequest: { pageNumber: FIRST_PAGE, pageSize: PAGE_SIZE },
      }),
    )

    nextTick(() => {
      replace(`${pathname}?${getQueryStringToSetDateRangeToQuery(searchParams, newDateRange)}`)
    })
  }

  return (
    <>
      <FiltersBlock handleChange={handleFiltersChange}>
        {pagination?.totalRecords > 0 && <DateFilterLimitInfo />}
      </FiltersBlock>

      <ContentBlock
        isLoading={isLoading}
        totalCount={pagination?.totalRecords}
        items={<TransactionItems />}
        noContent={
          <RichText
            field={t('no_redemptions_found')}
            components={{
              paragraph: ({ children }) => <>{children}</>,
              em: ({ children }) => <>{children}</>,
            }}
          />
        }
      />

      <PaginationBlock
        isShow={isPaginated}
        paginationProps={{
          total: pagination?.totalPages,
          current: pagination?.currentPage,
          onPageChange: handlePageChange,
        }}
      />

      {!isLoading && <TimeTable />}
    </>
  )
}
